import {
    type AllowedPartners,
    TemplateStatus,
    useExpertUniversityConfiguration,
    useExpertUniversityStore,
} from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    Button,
    Center,
    Container,
    Group,
    LoadingOverlay,
    Overlay,
    ScrollArea,
    SimpleGrid,
    Stack,
    Switch,
    Text,
} from "@mantine/core";
import { type FC, useEffect, useMemo, useState } from "react";

import { Warning } from "@/common-ui";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { LoadingAnimation } from "@/sales-tools/home-product/components/Icons";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { userCache } from "@/shared-utils/auth";

import type { EvaluationResultsErrorResponse, TranscriptContent } from "../api/evaluations/types";

import { EvaluationApiClient } from "../api/evaluations/evaluations-client";
import { getEvaluationResultsBySessionId, updateTrainingSession } from "../api/requests";
import { useEmployeeTrainingSessions } from "../hooks/useEmployeeTrainingSessions";
import { useTrainingProfiles } from "../hooks/useTrainingProfiles";
import {
    TrainingSessionChallengeResultStatus,
    TrainingSessionChallengeStatusEnum,
    type TrainingSessionEvaluationResults,
} from "../types";
import { isEvaluationTranscriptContent } from "../utils";
import classes from "./ExpertUniversityHome.module.css";
import { LaunchCallSandboxButton } from "./LaunchCallSandboxButton";
import { Sidebar } from "./Sidebar";
import TrainingEvaluationPage from "./TrainingEvaluationPage";
import { TrainingProfileTile } from "./TrainingProfileTile";

const evaluationApiClient = new EvaluationApiClient();
const PROFILE_TESTING_GROUP = "HQDOMAIN_APP-NON-VZW-APP-SOLUTO-TRAINING-ASSIST-";

export const ExpertUniversityHome: FC = () => {
    const logger = useLogger({ module: "Expert University Home" });
    const {
        trainingPartner: partner,
        taskInSandbox,
        setTaskInSandbox,
        setMostRecentSessionId,
    } = useExpertUniversityConfiguration();
    const shouldShowInProgressProfiles = userCache.groups.includes(PROFILE_TESTING_GROUP);
    const correlationId = crypto.randomUUID();
    const [evaluationResults, setEvaluationResults] = useState<
        TranscriptContent | EvaluationResultsErrorResponse | undefined
    >();
    const [isPending, setIsPending] = useState(false);
    const [showInProgressProfiles, setShowInProgressProfiles] = useState(shouldShowInProgressProfiles);
    const {
        eventBus: expertUniversitySdkEventBus,
        setReconnect,
        setSessionId,
        sessionId,
    } = useExpertUniversityStore.getState();
    setReconnect(false);

    useEffect(() => {
        const triggerEvaluationResults = expertUniversitySdkEventBus?.on(
            "trigger-evaluation-results",
            async (event) => {
                //After ending a 'call' in the sandbox environment, we do not want to fetch evaluation results as none exist
                if (taskInSandbox) {
                    setEvaluationResults(undefined);
                    return;
                }
                setIsPending(true);

                try {
                    const evaluationResults = await evaluationApiClient.triggerEvaluationBySessionId(event.sessionId);
                    if (isEvaluationTranscriptContent(evaluationResults)) {
                        trackFullStoryEvent("Evaluation Results Received", {
                            sessionId: event.sessionId ?? sessionId,
                            profileId: event.profileId,
                            evaluationResults: evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase(),
                            behaviorsPresent: evaluationResults.EvaluationDetails.logs?.behaviors_present,
                            maxBehaviors: evaluationResults.EvaluationDetails.logs?.max_behaviors,
                        });
                        logger.info(
                            {
                                challengeStatus:
                                    evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase() ===
                                    TrainingSessionChallengeResultStatus.CLEARED
                                        ? TrainingSessionChallengeStatusEnum.COMPLETE
                                        : TrainingSessionChallengeStatusEnum.NOT_COMPLETE,
                                evaluationResults: evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase(),
                                behaviorsPresent: evaluationResults.EvaluationDetails.logs?.behaviors_present,
                                maxBehaviors: evaluationResults.EvaluationDetails.logs?.max_behaviors,
                                sessionId: event.sessionId ?? sessionId,
                                profileId: event.profileId,
                            },
                            "Triggering evaluation results",
                        );
                        await updateTrainingSession({
                            SessionID: event.sessionId,
                            ChallengeId: event.profileId,
                            ChallengeStatus:
                                evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase() ===
                                TrainingSessionChallengeResultStatus.CLEARED
                                    ? TrainingSessionChallengeStatusEnum.COMPLETE
                                    : TrainingSessionChallengeStatusEnum.NOT_COMPLETE,
                            EmployeeId: userCache.employeeId,
                            SessionEndTime: new Date().toISOString(),
                        });
                    }
                    setEvaluationResults(evaluationResults);
                    setIsPending(false);
                } catch (error: any) {
                    logger.info(
                        { error: error.mesage, sessionId: event.sessionId ?? sessionId },
                        "Error triggering evaluation results",
                    );
                    trackFullStoryEvent("Evaluation Results Error", {
                        sessionId: event.sessionId ?? sessionId,
                        profileId: event.profileId,
                    });
                    setEvaluationResults({
                        ErrorCode: "EVALUATION_ERROR",
                        Message: "Failed to fetch evaluation results",
                    });
                    setIsPending(false);
                }
            },
        );
        return () => {
            triggerEvaluationResults?.();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const partnerTrainingChallengeProfiles = useTrainingProfiles("asurion", partner as AllowedPartners, correlationId);
    const trainingChallengeProfiles = showInProgressProfiles
        ? partnerTrainingChallengeProfiles
        : partnerTrainingChallengeProfiles?.filter((profile) => profile.status === TemplateStatus.IN_PRODUCTION);

    // We're using the presence of evaluationResults, which determines state of this component, to flip
    // the query key for training sessions - this forces a refetch of the sessions after evals viewed
    const viewFlag = useMemo(() => (evaluationResults === undefined ? "home" : "eval"), [evaluationResults]);
    const { data: expertTrainingSessions, isFetching: isFetchingTrainingSessions } = useEmployeeTrainingSessions(
        viewFlag,
        userCache.employeeId,
    );

    if (isPending || (isFetchingTrainingSessions && !evaluationResults)) {
        return (
            <LoadingOverlay
                visible={isPending || (isFetchingTrainingSessions && !evaluationResults)}
                loaderProps={{
                    children: (
                        <Container maw="50%">
                            <LoadingAnimation />
                            <Text>
                                {isPending
                                    ? "Loading results...it may take up to 20 seconds."
                                    : "Loading training sessions..."}
                            </Text>
                        </Container>
                    ),
                }}
                overlayProps={{
                    backgroundOpacity: 1,
                    color: "var(--exwo-color-app-bg)",
                }}
            />
        );
    }

    if (evaluationResults === undefined) {
        setSessionId(undefined);
        return (
            <div className={classes.background}>
                <div className={classes.container}>
                    <div className={classes.expertUniversityColumn}>
                        <Stack gap="1.5rem" h="100%">
                            <Group gap="4">
                                <Text
                                    fw={700}
                                    c="var(--mantine-color-dark-3)"
                                    fz="1.25rem"
                                    lh="1.5rem"
                                    style={{ flex: 1 }}
                                >
                                    Select a Profile
                                </Text>
                                <LaunchCallSandboxButton />
                            </Group>
                            {shouldShowInProgressProfiles && (
                                <Switch
                                    label="Show in progress profiles"
                                    checked={showInProgressProfiles}
                                    onChange={(event) => setShowInProgressProfiles(event.currentTarget.checked)}
                                    fw={700}
                                    pl="0.5rem"
                                    c="var(--mantine-color-dark-3)"
                                />
                            )}
                            <ScrollArea
                                offsetScrollbars
                                scrollbarSize={8}
                                styles={{
                                    viewport: { paddingRight: 16 },
                                }}
                            >
                                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} mt="0.5rem" ml="0.5rem" spacing="md">
                                    {trainingChallengeProfiles?.map((profile) => {
                                        const trainingProfileSessions = expertTrainingSessions?.find(
                                            (trainingSession) => trainingSession.challengeId === profile.id,
                                        );
                                        let latestSession: TrainingSessionEvaluationResults | undefined =
                                            trainingProfileSessions?.evaluationDetails?.[0];

                                        // if there is more than one training session associated with the expert,
                                        // grab the latest session by timestamp
                                        if (
                                            trainingProfileSessions?.challengeTaken &&
                                            trainingProfileSessions?.evaluationDetails.length > 1
                                        ) {
                                            latestSession = trainingProfileSessions.evaluationDetails.reduce((a, b) => {
                                                const previousSessionTime = a.Timestamp ?? 0;
                                                const currentSessionTime = b.Timestamp ?? 0;

                                                return new Date(previousSessionTime) > new Date(currentSessionTime)
                                                    ? a
                                                    : b;
                                            });
                                        }
                                        return (
                                            <TrainingProfileTile
                                                key={profile.id}
                                                id={profile.id}
                                                description={profile.description}
                                                challengeTaken={trainingProfileSessions?.challengeTaken ?? false}
                                                challengeStatus={latestSession?.Results}
                                                onStart={() => {
                                                    trackFullStoryEvent("Expert_University_Training_Session_Started", {
                                                        profileId: profile.id,
                                                    });
                                                    if (expertUniversitySdkEventBus) {
                                                        logger.info(
                                                            { profileId: profile.id },
                                                            "CALL CREATE TRAINING TASK",
                                                        );
                                                        setTaskInSandbox(false);
                                                        expertUniversitySdkEventBus.emit("start-simulation-training", {
                                                            profileId: profile.id,
                                                            partner: partner as AllowedPartners,
                                                        });
                                                    }
                                                }}
                                                onViewResults={async () => {
                                                    if (latestSession?.SessionId) {
                                                        setIsPending(true);
                                                        const evaluationResults = await getEvaluationResultsBySessionId(
                                                            latestSession.SessionId,
                                                        );
                                                        setEvaluationResults(evaluationResults?.items[0]);
                                                        setIsPending(false);
                                                        setMostRecentSessionId(latestSession.SessionId);
                                                    }
                                                    trackFullStoryEvent("Expert_University_View_Results_Called", {
                                                        sessionId: latestSession?.SessionId ?? sessionId,
                                                        profileId: profile.id,
                                                    });
                                                    logger.info(
                                                        {
                                                            profileId: profile.id,
                                                            sessionId: latestSession?.SessionId ?? sessionId,
                                                        },
                                                        "CALL VIEW RESULTS",
                                                    );
                                                }}
                                            />
                                        );
                                    })}
                                </SimpleGrid>
                            </ScrollArea>
                        </Stack>
                    </div>
                    <div className={classes.expertUniversityColumn}>
                        <Sidebar partner={partner as AllowedPartners} />
                    </div>
                </div>
            </div>
        );
    }

    if (isEvaluationTranscriptContent(evaluationResults)) {
        return (
            <div className={classes.background}>
                <TrainingEvaluationPage
                    evaluationResults={evaluationResults}
                    onCompleteEvaluation={() => {
                        setEvaluationResults(undefined);
                    }}
                />
            </div>
        );
    }

    if (evaluationResults.ErrorCode) {
        return (
            <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                <Center
                    style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
                    className={sharedStyles.animateScaleIn}
                >
                    <Warning width={80} height={80} />
                    <Text fw="700" ta="center" size="xl">
                        There was an issue evaluating your results, please select a profile and try again.
                    </Text>
                    <Button
                        onClick={() => {
                            setEvaluationResults(undefined);
                        }}
                    >
                        Return Home
                    </Button>
                </Center>
            </Overlay>
        );
    }

    return (
        <Center
            style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
            className={sharedStyles.animateScaleIn}
        >
            <Text fw="700" ta="center" size="xl">
                Welcome to Expert University
            </Text>
        </Center>
    );
};

export default ExpertUniversityHome;
